/**
 * Per-tab "this tab is the recorder" flag, used to distinguish a
 * refresh in the same tab (auto-resume mic capture, IDB chunks replay)
 * from a cold mount on a different tab/device (don't hijack the mic;
 * show a recovery banner).
 *
 * Stored in sessionStorage because:
 *   - It must survive a refresh in the same tab → localStorage and
 *     sessionStorage both qualify here.
 *   - It must NOT leak to other tabs of the same browser → only
 *     sessionStorage. localStorage would let a tab the doctor never
 *     opened claim ownership of an active recording.
 *   - It must NOT survive a tab close → only sessionStorage. A
 *     reopened tab post-crash should land on the recovery banner,
 *     not silently steal the mic.
 *
 * Companion to the IDB chunk store (`audioChunkStore`) which holds
 * the actual audio bytes. The marker tells us "this tab was the
 * recorder"; IDB tells us "this device has the audio". Both signals
 * inform the cold-mount recovery flow.
 */

const KEY_PREFIX = 'maie.recordingTab.';
const key = (sessionId: string) => `${KEY_PREFIX}${sessionId}`;

export const markTabAsRecorder = (sessionId: string): void => {
  try {
    sessionStorage.setItem(key(sessionId), String(Date.now()));
  } catch {
    /* sessionStorage disabled — best-effort; the recovery flow falls
       back to showing the banner, which is the safer default. */
  }
};

export const isTabRecorder = (sessionId: string): boolean => {
  try {
    return sessionStorage.getItem(key(sessionId)) !== null;
  } catch {
    return false;
  }
};

export const clearTabRecorderMark = (sessionId: string): void => {
  try {
    sessionStorage.removeItem(key(sessionId));
  } catch {
    /* see markTabAsRecorder — non-fatal */
  }
};
